import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useFieldClass from "../../../functions/useFieldClass";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectSkidConstrution,
  selectGeneralStation,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import SaveBanner from "./SaveBanner";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

const SkidConstruction = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "skidConstruction";

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectSkidConstrution);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const generalSpecsDetails = useSelector(selectGeneralStation);

  const stationColor =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)?.Color ||
    "";

  // Get station type for the current active tab
  const stationType =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)?.StationType ||
    "";
  
  // Check if station type is SkyHarvester SH 80
  const showAdditionalSkidForVMS = stationType === "SkyHarvester SH 80";

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        skidConstruction: jobDetails.SkidConstruction || "",
        mustFitInShippingContainer: jobDetails.MustFitInShippingContainer || "",
        customSkidConstructionDetailss:
          jobDetails.CustomSkidConstructionDetailss || "",
        liftingPoints: jobDetails.LiftingPoints || "",
        additionalSkidForVMS: jobDetails.AdditionalSkidForVMS || "",
        coatingForSkid: jobDetails.CoatingForSkid || "",
        skidColor: jobDetails.SkidColor || "",
        specialCoatingUnderSkid: jobDetails.SpecialCoatingUnderSkid || "",
        levelingKit: jobDetails.LevelingKit || "",
        skidComments: jobDetails.SkidComments || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  /****************Add dropdown options here*************************/
  const skidConstructionOptions =
    options?.filter((option) => option.DropdownType === "Skid Construction")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const coatingForSkidOptions =
    options?.filter((option) => option.DropdownType === "Coating For Skid")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const skidColorOptions =
    options?.filter((option) => option.DropdownType === "Skid Color")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const additionalSkidForVMSOptions =
    options?.filter((option) => option.DropdownType === "Additional Skid For VMS")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /***************************************************************** */

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
          <div className="mechanical-responsive-grid">
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="skidConstruction">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SkidConstruction")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Skid Construction</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="skidConstruction"
                      value={formData.skidConstruction || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Skid Construction", skidConstructionOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SkidConstruction",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.skidConstruction}
                      onChange={handleChange}
                    >
                      <option value="">Select Skid Construction</option>
                      {skidConstructionOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.skidConstruction, skidConstructionOptions) &&
                        formData.skidConstruction && (
                          <option value={formData.skidConstruction} style={{ color: "red" }}>
                            *{formData.skidConstruction}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="coatingForSkid">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("CoatingForSkid")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Coating For Skid</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="coatingForSkid"
                      value={formData.coatingForSkid || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Coating For Skid", coatingForSkidOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "CoatingForSkid",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.coatingForSkid}
                      onChange={handleChange}
                    >
                      <option value="">Select Coating For Skid</option>
                      {coatingForSkidOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.coatingForSkid, coatingForSkidOptions) &&
                        formData.coatingForSkid && (
                          <option value={formData.coatingForSkid} style={{ color: "red" }}>
                            *{formData.coatingForSkid}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="mustFitInShippingContainer">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MustFitInShippingContainer")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Must Fit In Shipping Container</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="mustFitInShippingContainer"
                      value={formData.mustFitInShippingContainer || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Must Fit In Shipping Container",
                          genericOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "MustFitInShippingContainer",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.mustFitInShippingContainer}
                      onChange={handleChange}
                    >
                      <option value="">
                        Select Must Fit In Shipping Container
                      </option>
                      {genericOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.mustFitInShippingContainer, genericOptions) &&
                        formData.mustFitInShippingContainer && (
                          <option value={formData.mustFitInShippingContainer} style={{ color: "red" }}>
                            *{formData.mustFitInShippingContainer}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="skidColor">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SkidColor")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Skid Color</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="skidColor"
                      value={formData.skidColor || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Skid Color", skidColorOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SkidColor",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.skidColor}
                      onChange={handleChange}
                    >
                      <option value="">Select Skid Color</option>
                      {skidColorOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText === "Same as Station"
                            ? `Same as Station (${stationColor})`
                            : option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.skidColor, skidColorOptions) &&
                        formData.skidColor && (
                          <option value={formData.skidColor} style={{ color: "red" }}>
                            *{formData.skidColor}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="customSkidConstructionDetailss">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("CustomSkidConstructionDetailss")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Custom Skid Construction Details</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="customSkidConstructionDetailss"
                      value={formData.customSkidConstructionDetailss || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Custom Skid Construction Detailss",
                          [],
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "CustomSkidConstructionDetailss",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.customSkidConstructionDetailss}
                      onChange={handleChange}
                      placeholder="Enter Custom Skid Construction Details"
                    />
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="specialCoatingUnderSkid">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SpecialCoatingUnderSkid")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Special Coating Under Skid</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="specialCoatingUnderSkid"
                      value={formData.specialCoatingUnderSkid || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Special Coating Under Skid", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SpecialCoatingUnderSkid",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.specialCoatingUnderSkid}
                      onChange={handleChange}
                      placeholder="Enter Special Coating Under Skid"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="liftingPoints">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("LiftingPoints")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Lifting Points</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="liftingPoints"
                      value={formData.liftingPoints || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Lifting Points", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "LiftingPoints",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.liftingPoints}
                      onChange={handleChange}
                      placeholder="Enter Lifting Points"
                    />
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="levelingKit">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("LevelingKit")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Leveling Kit</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="levelingKit"
                      value={formData.levelingKit || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Leveling Kit", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "LevelingKit",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.levelingKit}
                      onChange={handleChange}
                      placeholder="Enter Leveling Kit"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            {showAdditionalSkidForVMS && (
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="additionalSkidForVMS">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("AdditionalSkidForVMS")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Additional Skid For VMS</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="additionalSkidForVMS"
                        value={formData.additionalSkidForVMS || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "Additional Skid For VMS",
                            additionalSkidForVMSOptions,
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "AdditionalSkidForVMS",
                              tableKey,
                            }
                          )
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.additionalSkidForVMS}
                        onChange={handleChange}
                      >
                        <option value="">Select Additional Skid For VMS</option>
                        {additionalSkidForVMSOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(formData.additionalSkidForVMS, additionalSkidForVMSOptions) &&
                          formData.additionalSkidForVMS && (
                            <option value={formData.additionalSkidForVMS} style={{ color: "red" }}>
                              *{formData.additionalSkidForVMS}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>

                <Col />
              </Row>
            )}
            <Row className="mb-3 mechanical-form-full-width">
              <Col>
                <Form.Group controlId="skidComments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SkidComments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="skidComments"
                    value={formData.skidComments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Skid Comments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SkidComments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.skidComments}
                    onChange={handleChange}
                    placeholder="Enter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkidConstruction;
